/**
 * Client for the event-day door routes: /event-day/:project_id/events/:event_id/... (ans-backend, kal shape).
 *
 * It takes its credentials from a function so the same client serves two callers: a signed-in planner or owner (the normal
 * session token and active project) and a kiosk device (an event-bound token kept in kioskSession.ts).
 */
import { getApiKey, getApiUrl, getProjectId } from './PlanningApiClient';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { ApiError, buildApiError } from '../utils/apiErrors';
import type { DoorCounts, DoorFavour, DoorFilter, DoorGuest, WalkInInput } from '../types/Door';

export interface DoorCredentials {
  token: string;
  projectId: string;
}

/** The signed-in planner or owner's credentials: the normal session token and the active project. */
export function staffCredentials(): DoorCredentials | null {
  let token: string | null = null;
  try { token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN); } catch { /* storage unavailable */ }
  const projectId = getProjectId();
  return token && projectId ? { token, projectId } : null;
}

export type DoorCredentialSource = () => DoorCredentials | null;

export interface DoorApi {
  listGuests(eventId: string, opts?: { q?: string; filter?: DoorFilter }): Promise<DoorGuest[]>;
  counts(eventId: string): Promise<DoorCounts>;
  checkIn(eventId: string, guestId: string): Promise<{ guest: DoorGuest; changed: boolean; counts: DoorCounts }>;
  undoCheckIn(eventId: string, guestId: string): Promise<{ guest: DoorGuest; changed: boolean; counts: DoorCounts }>;
  addWalkIn(eventId: string, input: WalkInInput): Promise<{ guest: DoorGuest; created: boolean; counts: DoorCounts }>;
  listFavours(eventId: string): Promise<DoorFavour[]>;
  /** Hands the favour over once; the idempotency key makes a retry after a dropped connection safe. */
  handOver(eventId: string, assignmentId: string, idempotencyKey?: string): Promise<{ favour: DoorFavour; changed: boolean }>;
  undoHandOver(eventId: string, assignmentId: string): Promise<{ favour: DoorFavour; changed: boolean }>;
}

export function createDoorApi(credentials: DoorCredentialSource): DoorApi {
  async function request<T>(eventId: string, suffix: string, init: { method?: string; body?: unknown; headers?: Record<string, string> } = {}): Promise<T> {
    const creds = credentials();
    if (!creds) throw new ApiError('Your session has ended. Please sign in again.', { status: 401 });
    let res: Response;
    try {
      res = await fetch(`${getApiUrl()}/event-day/${creds.projectId}/events/${eventId}${suffix}`, {
        method: init.method ?? 'GET',
        headers: {
          ...init.headers,
          'Content-Type': 'application/json',
          'x-api-key': getApiKey(),
          Authorization: `Bearer ${creds.token}`,
        },
        body: init.body != null ? JSON.stringify(init.body) : undefined,
        cache: 'no-store',
      });
    } catch {
      throw ApiError.offline();
    }
    if (!res.ok) throw buildApiError(res.status, await res.json().catch(() => null));
    return res.json();
  }

  return {
    async listGuests(eventId, opts = {}) {
      const params = new URLSearchParams();
      if (opts.q?.trim()) params.set('q', opts.q.trim());
      if (opts.filter && opts.filter !== 'all') params.set('filter', opts.filter);
      const qs = params.toString();
      return (await request<{ guests: DoorGuest[] }>(eventId, `/guests${qs ? `?${qs}` : ''}`)).guests;
    },
    async counts(eventId) {
      return (await request<{ counts: DoorCounts }>(eventId, '/dashboard')).counts;
    },
    checkIn: (eventId, guestId) => request(eventId, `/guests/${encodeURIComponent(guestId)}/check-in`, { method: 'POST' }),
    undoCheckIn: (eventId, guestId) => request(eventId, `/guests/${encodeURIComponent(guestId)}/check-in`, { method: 'DELETE' }),
    addWalkIn: (eventId, input) => request(eventId, '/walk-ins', { method: 'POST', body: input }),
    async listFavours(eventId) {
      return (await request<{ favours: DoorFavour[] }>(eventId, '/favours')).favours;
    },
    handOver: (eventId, id, idempotencyKey) => request(eventId, `/favours/${encodeURIComponent(id)}/hand-over`, {
      method: 'POST', ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
    }),
    undoHandOver: (eventId, id) => request(eventId, `/favours/${encodeURIComponent(id)}/hand-over`, { method: 'DELETE' }),
  };
}
